import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ScannerSection } from '../scanner/ScannerSection';
import SkuAutocompleteInput from '../shared/SkuAutocompleteInput';
import { useSkuAutocomplete } from '../../hooks/useSkuAutocomplete';
import { useSkuValidation } from '../../hooks/useSkuValidation';
import { runOcrAbCompare } from '../../services/devAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import { SKU_PREFIX } from '../../constants/app.constants';
import type { OcrAbArmResult, OcrAbCompareResponse } from '../../types/apiTypes';
import {
  scoreOcrAbArm,
  computeAccuracyStats,
  computeLatencyStats,
} from './ocrAbScoring';
import '../layout/DevPanel.css';
import '../labels/PrintBoxLabel.css';
import './OcrAbTest.css';

interface OcrAbTrial {
  id: string;
  capturedAt: string;
  expectedSku: string;
  expectedShelf: string;
  text: OcrAbArmResult;
  document: OcrAbArmResult;
  textSkuMatched: boolean | null;
  textShelfMatched: boolean | null;
  documentSkuMatched: boolean | null;
  documentShelfMatched: boolean | null;
}

function formatMatch(value: boolean | null): string {
  if (value === null) return '—';
  return value ? 'PASS' : 'FAIL';
}

function MatchBadge({ value }: { value: boolean | null }): React.ReactElement {
  if (value === null) {
    return <span className="ocr-ab-badge ocr-ab-badge-na">—</span>;
  }
  return (
    <span className={`ocr-ab-badge ${value ? 'ocr-ab-badge-pass' : 'ocr-ab-badge-fail'}`}>
      {value ? 'PASS' : 'FAIL'}
    </span>
  );
}

function ArmPanel({
  title,
  arm,
  skuMatched,
  shelfMatched,
}: {
  title: string;
  arm: OcrAbArmResult;
  skuMatched: boolean | null;
  shelfMatched: boolean | null;
}): React.ReactElement {
  return (
    <div className="ocr-ab-arm">
      <h4 className="ocr-ab-arm-title">{title}</h4>
      <p className="ocr-ab-latency">{arm.latencyMs} ms</p>
      <div className="ocr-ab-badges">
        <span>SKU <MatchBadge value={skuMatched} /></span>
        <span>Shelf <MatchBadge value={shelfMatched} /></span>
      </div>
      <pre className="ocr-ab-raw-text">{arm.text ?? '(no text)'}</pre>
    </div>
  );
}

const OcrAbTest: React.FC = () => {
  const [expectedSku, setExpectedSku] = useState('');
  const [expectedShelf, setExpectedShelf] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [latest, setLatest] = useState<OcrAbTrial | null>(null);
  const [trials, setTrials] = useState<OcrAbTrial[]>([]);

  const [showSkuModal, setShowSkuModal] = useState(false);
  const [manualSKU, setManualSKU] = useState(SKU_PREFIX);
  const [skuModalError, setSkuModalError] = useState<string | null>(null);
  const skuInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const skipAutocompleteOpenRef = useRef(false);

  const { allSkus, validateSku } = useSkuValidation();
  const {
    filteredSkus,
    showAutocomplete,
    setShowAutocomplete,
    selectedIndex,
    setSelectedIndex,
  } = useSkuAutocomplete({
    value: manualSKU,
    allSkus,
    prefix: SKU_PREFIX,
    skipAutocompleteOpenRef,
    inputRef: skuInputRef,
    autocompleteRef,
  });

  const canScore = expectedSku.trim().length > 0 || expectedShelf.trim().length > 0;

  const openSkuModal = (): void => {
    if (loading) return;
    setSkuModalError(null);
    setManualSKU(expectedSku || SKU_PREFIX);
    setShowSkuModal(true);
    setTimeout(() => {
      if (skuInputRef.current) {
        skuInputRef.current.focus();
        const len = skuInputRef.current.value.length;
        skuInputRef.current.setSelectionRange(len, len);
      }
    }, 100);
  };

  const closeSkuModal = (): void => {
    setShowSkuModal(false);
    setManualSKU(SKU_PREFIX);
    setSkuModalError(null);
    setShowAutocomplete(false);
    setSelectedIndex(-1);
  };

  const applyExpectedSku = (sku: string): void => {
    const skuValue = sku.trim().toUpperCase();
    if (!validateSku(skuValue)) {
      setSkuModalError(`SKU ${skuValue} Not found in Product DB`);
      return;
    }
    skipAutocompleteOpenRef.current = true;
    setExpectedSku(skuValue);
    setShowAutocomplete(false);
    setSelectedIndex(-1);
    closeSkuModal();
  };

  const handleSKUInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setSkuModalError(null);
    if (value.length < SKU_PREFIX.length) {
      setManualSKU(SKU_PREFIX);
    } else if (!value.startsWith(SKU_PREFIX)) {
      setManualSKU(SKU_PREFIX + value.substring(SKU_PREFIX.length));
    } else {
      setManualSKU(value.toUpperCase());
    }
  };

  const handleSKUInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!showAutocomplete || filteredSkus.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < filteredSkus.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      applyExpectedSku(filteredSkus[selectedIndex]!);
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
    }
  };

  const handleSetSkuFromModal = (): void => {
    if (manualSKU.trim() && manualSKU.trim().length > SKU_PREFIX.length) {
      applyExpectedSku(manualSKU);
    } else {
      setSkuModalError('Please enter a valid SKU.');
    }
  };

  const kpis = useMemo(() => {
    const textSku = computeAccuracyStats(trials.map((t) => t.textSkuMatched));
    const textShelf = computeAccuracyStats(trials.map((t) => t.textShelfMatched));
    const docSku = computeAccuracyStats(trials.map((t) => t.documentSkuMatched));
    const docShelf = computeAccuracyStats(trials.map((t) => t.documentShelfMatched));
    const textLatency = computeLatencyStats(trials.map((t) => t.text.latencyMs));
    const docLatency = computeLatencyStats(trials.map((t) => t.document.latencyMs));
    return { textSku, textShelf, docSku, docShelf, textLatency, docLatency };
  }, [trials]);

  const buildTrial = (compare: OcrAbCompareResponse): OcrAbTrial => {
    const textScore = scoreOcrAbArm(compare.text.text, expectedSku, expectedShelf);
    const documentScore = scoreOcrAbArm(compare.document.text, expectedSku, expectedShelf);
    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      capturedAt: new Date().toISOString(),
      expectedSku: expectedSku.trim(),
      expectedShelf: expectedShelf.trim(),
      text: compare.text,
      document: compare.document,
      textSkuMatched: textScore.skuMatched,
      textShelfMatched: textScore.shelfMatched,
      documentSkuMatched: documentScore.skuMatched,
      documentShelfMatched: documentScore.shelfMatched,
    };
  };

  const handleCapture = async (imageSrc: string | null): Promise<boolean> => {
    if (!imageSrc) {
      setError('No image captured');
      return false;
    }
    if (!canScore) {
      setError('Enter an expected SKU and/or shelf before capturing');
      return false;
    }

    try {
      setLoading(true);
      setError(null);
      const compare = await runOcrAbCompare(imageSrc);
      const trial = buildTrial(compare);
      setLatest(trial);
      setTrials((prev) => [trial, ...prev]);
      return true;
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'OCR A/B compare failed'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (): void => {
    const payload = {
      exportedAt: new Date().toISOString(),
      trialCount: trials.length,
      trials,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ocr-ab-trials-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatAccuracy = (
    stats: ReturnType<typeof computeAccuracyStats>
  ): string => {
    if (!stats) return '—';
    return `${stats.percent}% (${stats.matched}/${stats.total})`;
  };

  const formatLatency = (
    stats: ReturnType<typeof computeLatencyStats>
  ): string => {
    if (!stats) return '—';
    return `mean ${stats.meanMs} ms · p50 ${stats.p50Ms} ms (n=${stats.count})`;
  };

  return (
    <div className="dev-panel-container ocr-ab-container">
      <h2>OCR A/B Test</h2>
      <p className="ocr-ab-description">
        Paired bake-off: <strong>TEXT_DETECTION</strong> (current) vs{' '}
        <strong>DOCUMENT_TEXT_DETECTION</strong> on the same webcam frame.
        Production <code>/api/ocr</code> is unchanged. Enter expected SKU and/or shelf, then capture.
      </p>
      <p className="ocr-ab-back">
        <Link to="/dev">← Dev Panel</Link>
      </p>

      <div className="dev-panel-section ocr-ab-expected">
        <h3 className="dev-panel-section-title">Expected values</h3>
        <span className="ocr-ab-field-label">Expected SKU</span>
        <div className="ocr-ab-sku-picker">
          <button
            type="button"
            className={`ocr-ab-sku-value ${!expectedSku ? 'ocr-ab-sku-empty' : ''}`}
            onClick={openSkuModal}
            disabled={loading}
          >
            {expectedSku || 'Click to enter SKU…'}
          </button>
          {expectedSku && (
            <button
              type="button"
              className="ocr-ab-sku-clear"
              onClick={() => setExpectedSku('')}
              disabled={loading}
              title="Clear SKU"
            >
              ×
            </button>
          )}
        </div>
        <label htmlFor="ocr-ab-expected-shelf">Expected shelf</label>
        <input
          id="ocr-ab-expected-shelf"
          type="text"
          value={expectedShelf}
          onChange={(e) => setExpectedShelf(e.target.value)}
          placeholder="e.g. 6-J-2"
          disabled={loading}
        />
      </div>

      {showSkuModal && (
        <div className="pbl-modal-overlay" onClick={closeSkuModal}>
          <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="pbl-modal-close" onClick={closeSkuModal}>
              ×
            </button>
            <div className="pbl-modal-title">Enter Expected SKU</div>
            <div className="pbl-manual-popup">
              <SkuAutocompleteInput
                inputRef={skuInputRef}
                autocompleteRef={autocompleteRef}
                value={manualSKU}
                onChange={handleSKUInputChange}
                onKeyDown={handleSKUInputKeyDown}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && selectedIndex < 0) handleSetSkuFromModal();
                }}
                placeholder={`${SKU_PREFIX}...`}
                showAutocomplete={showAutocomplete}
                filteredSkus={filteredSkus}
                selectedIndex={selectedIndex}
                onSelectSku={applyExpectedSku}
                onHoverIndex={setSelectedIndex}
              />
              <button
                type="button"
                onClick={handleSetSkuFromModal}
                className="pbl-submit-button"
                disabled={!manualSKU.trim() || manualSKU.trim().length <= SKU_PREFIX.length}
              >
                Set SKU
              </button>
              {skuModalError && <div className="ocr-ab-sku-modal-error">{skuModalError}</div>}
            </div>
          </div>
        </div>
      )}

      <div className="dev-panel-section ocr-ab-scanner">
        <h3 className="dev-panel-section-title">Capture</h3>
        {loading && <p className="ocr-ab-loading">Running both Vision modes…</p>}
        <ScannerSection mode="barcode" onCapture={handleCapture} />
      </div>

      {error && <div className="dev-panel-error">{error}</div>}

      {latest && (
        <div className="ocr-ab-latest">
          <h3 className="dev-panel-section-title">Latest trial</h3>
          <p className="ocr-ab-expected-summary">
            Expected SKU: <code>{latest.expectedSku || '—'}</code>
            {' · '}
            Shelf: <code>{latest.expectedShelf || '—'}</code>
          </p>
          <div className="ocr-ab-side-by-side">
            <ArmPanel
              title="TEXT_DETECTION (current)"
              arm={latest.text}
              skuMatched={latest.textSkuMatched}
              shelfMatched={latest.textShelfMatched}
            />
            <ArmPanel
              title="DOCUMENT_TEXT_DETECTION"
              arm={latest.document}
              skuMatched={latest.documentSkuMatched}
              shelfMatched={latest.documentShelfMatched}
            />
          </div>
        </div>
      )}

      {trials.length > 0 && (
        <>
          <div className="ocr-ab-kpis">
            <h3 className="dev-panel-section-title">Session KPIs ({trials.length} trials)</h3>
            <div className="ocr-ab-kpi-grid">
              <div className="ocr-ab-kpi-card">
                <h4>TEXT_DETECTION</h4>
                <p>SKU accuracy: {formatAccuracy(kpis.textSku)}</p>
                <p>Shelf accuracy: {formatAccuracy(kpis.textShelf)}</p>
                <p>Latency: {formatLatency(kpis.textLatency)}</p>
              </div>
              <div className="ocr-ab-kpi-card">
                <h4>DOCUMENT_TEXT_DETECTION</h4>
                <p>SKU accuracy: {formatAccuracy(kpis.docSku)}</p>
                <p>Shelf accuracy: {formatAccuracy(kpis.docShelf)}</p>
                <p>Latency: {formatLatency(kpis.docLatency)}</p>
              </div>
            </div>
            <button type="button" className="dev-panel-button" onClick={handleExport}>
              Export JSON
            </button>
            <button
              type="button"
              className="dev-panel-button ocr-ab-clear"
              onClick={() => {
                setTrials([]);
                setLatest(null);
              }}
            >
              Clear session
            </button>
          </div>

          <div className="ocr-ab-history">
            <h3 className="dev-panel-section-title">Session trials</h3>
            <div className="ocr-ab-table-wrap">
              <table className="dev-panel-backup-table ocr-ab-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Expected</th>
                    <th>TEXT SKU</th>
                    <th>TEXT shelf</th>
                    <th>TEXT ms</th>
                    <th>DOC SKU</th>
                    <th>DOC shelf</th>
                    <th>DOC ms</th>
                  </tr>
                </thead>
                <tbody>
                  {trials.map((t) => (
                    <tr key={t.id}>
                      <td className="ocr-ab-time">{new Date(t.capturedAt).toLocaleTimeString()}</td>
                      <td className="ocr-ab-expected-cell">
                        {[t.expectedSku, t.expectedShelf].filter(Boolean).join(' / ') || '—'}
                      </td>
                      <td>{formatMatch(t.textSkuMatched)}</td>
                      <td>{formatMatch(t.textShelfMatched)}</td>
                      <td>{t.text.latencyMs}</td>
                      <td>{formatMatch(t.documentSkuMatched)}</td>
                      <td>{formatMatch(t.documentShelfMatched)}</td>
                      <td>{t.document.latencyMs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OcrAbTest;
